    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Lifecycle" title="Lifecycle Control Center" subtitle="Review employee action requests, onboarding blockers, and offboarding checklists.">
        <div actions>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'actions', label: 'Action Requests', icon: 'fa-solid fa-list-check', badge: actions.length },
          { key: 'onboarding', label: 'Open Onboarding', icon: 'fa-solid fa-user-plus', badge: dashboard.activeOnboarding?.length || 0 },
          { key: 'offboarding', label: 'Open Offboarding', icon: 'fa-solid fa-user-minus', badge: dashboard.activeOffboarding?.length || 0 }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <section class="lifecycle-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'onboarding'" title="Open Onboarding" subtitle="Employees still moving through activation gates." [resultText]="(dashboard.activeOnboarding?.length || 0) + ' plans'">
          <p-table [value]="dashboard.activeOnboarding || []" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '820px' }">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Status</th><th>Target</th><th>Tasks</th><th></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-plan>
              <tr>
                <td><strong>{{ plan.employeeName || '-' }}</strong><br><small>{{ plan.employeeNumber || plan.employeeId }}</small></td>
                <td><p-tag [value]="format(plan.planStatus)" [severity]="planSeverity(plan.planStatus)"></p-tag></td>
                <td>{{ plan.targetCompletionDate || '-' }}</td>
                <td>{{ closedTasks(plan.tasks) }} / {{ plan.tasks?.length || 0 }}</td>
                <td><button pButton type="button" icon="pi pi-eye" [rounded]="true" [text]="true" [routerLink]="['/hr/employees', plan.employeeId]"></button></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">No open onboarding plans.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'offboarding'" title="Open Offboarding" subtitle="Termination-approved employees with exit work still open." [resultText]="(dashboard.activeOffboarding?.length || 0) + ' plans'">
          <p-table [value]="dashboard.activeOffboarding || []" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '820px' }">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Status</th><th>Target</th><th>Tasks</th><th></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-plan>
              <tr>
                <td><strong>{{ plan.employeeName || '-' }}</strong><br><small>{{ plan.employeeNumber || plan.employeeId }}</small></td>
                <td><p-tag [value]="format(plan.planStatus)" [severity]="planSeverity(plan.planStatus)"></p-tag></td>
                <td>{{ plan.targetCompletionDate || '-' }}</td>
                <td>{{ closedTasks(plan.tasks) }} / {{ plan.tasks?.length || 0 }}</td>
                <td><button pButton type="button" icon="pi pi-eye" [rounded]="true" [text]="true" [routerLink]="['/hr/employees', plan.employeeId]"></button></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">No open offboarding plans.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      <app-page-data-panel [hidden]="activeListTab !== 'actions'" title="Action Requests" subtitle="Transfers, compensation placeholders, and termination approvals." [resultText]="actions.length + ' requests'">
        <div class="filter-row">
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-circle-check"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="actionStatusFilter" [options]="statusOptions" [(ngModel)]="statusFilter" [showClear]="true" styleClass="w-full"></p-select>
              <label for="actionStatusFilter">Request status</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-arrows-rotate"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="actionTypeFilter" [options]="actionTypeOptions" [(ngModel)]="actionTypeFilter" [showClear]="true" styleClass="w-full"></p-select>
              <label for="actionTypeFilter">Action type</label>
            </p-iftaLabel>
          </p-inputGroup>
          <button pButton type="button" icon="pi pi-search" label="Filter" (click)="loadActions()"></button>
          <button pButton type="button" icon="pi pi-filter-slash" label="Clear" severity="secondary" [outlined]="true" (click)="clearFilters()"></button>
        </div>

        <p-table [value]="actions" [loading]="actionsLoading" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1120px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Employee</th>
              <th>Action</th>
              <th>Status</th>
              <th>Effective</th>
              <th>Current</th>
              <th>Requested</th>
              <th>Reason</th>
              <th style="width: 9rem">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-action>
            <tr>
              <td><strong>{{ action.employeeName || '-' }}</strong><br><small>{{ action.employeeNumber || action.employeeId }}</small></td>
              <td>{{ format(action.actionType) }}</td>
              <td><p-tag [value]="format(action.requestStatus)" [severity]="actionSeverity(action.requestStatus)"></p-tag></td>
              <td>{{ action.effectiveDate || '-' }}</td>
              <td>{{ currentSummary(action) }}</td>
              <td>{{ requestedSummary(action) }}</td>
              <td>{{ action.reason || '-' }}</td>
              <td>
                <button pButton type="button" icon="pi pi-eye" [rounded]="true" [text]="true" [routerLink]="['/hr/employees', action.employeeId]"></button>
                @if (canManageLifecycle && action.requestStatus === 'PENDING') {
                  <button pButton type="button" icon="pi pi-check" severity="success" [rounded]="true" [text]="true" (click)="approve(action)"></button>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8">No lifecycle action requests found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
